/**
 * Saldify API-Client.
 *
 * Sicherheit:
 * - Der Access-Token wird NUR im Speicher gehalten (nicht in localStorage),
 *   damit ihn XSS nicht auslesen kann.
 * - Das Refresh-Token liegt ausschliesslich im httpOnly-Cookie und wird vom
 *   Browser automatisch mitgesendet (credentials: 'include').
 * - Bei 401 wird einmalig ein stiller Refresh versucht.
 */

// API-Basis-URL: in Dev lokaler Port, in Prod relativ über /api (nginx-Proxy,
// gleiche Domain -> kein CORS). VITE_API_URL kann beides explizit überschreiben.
export const API_URL =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://localhost:4000' : '/api');

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
}

export interface CompanySummary {
  id: string;
  name: string;
  plan: string;
  role: string;
}

export interface CompanyUsage {
  plan: string;
  label: string;
  period: string;
  limits: { invoices: number | null; receipts: number | null; users: number | null; customers: number | null };
  used: { invoices: number; receipts: number };
  features: string[];
  subscription?: { status: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean };
}

interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    /** Zusatzfelder aus der Fehlerantwort (z.B. jahr, preisCents). */
    public data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function parseError(res: Response): Promise<never> {
  let message = 'Es ist ein Fehler aufgetreten.';
  let code: string | undefined;
  // Zusatzfelder durchreichen — manche Fehler tragen Nutzdaten, etwa das
  // gesperrte Steuerjahr samt Preis.
  let extra: Record<string, unknown> | undefined;
  try {
    const data = (await res.json()) as Record<string, unknown> & { error?: string; code?: string };
    if (data.error) message = data.error;
    if (data.code) code = data.code;
    extra = data;
  } catch {
    /* ignore */
  }
  throw new ApiError(res.status, message, code, extra);
}

/**
 * Fetch-Wrapper: haengt den Access-Token an, sendet Cookies mit und
 * erneuert bei 401 einmalig automatisch die Sitzung.
 */
async function request<T>(path: string, options: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(options.headers);
  // Content-Type NUR setzen, wenn es einen Body gibt — sonst lehnt Fastify
  // DELETE/GET mit "Body cannot be empty when content-type is application/json" ab.
  if (options.body != null) headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && retry && path !== '/auth/refresh' && path !== '/auth/login') {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, options, false);
  }

  if (!res.ok) return parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Wie request(), aber liefert die Antwort als Blob (für PDF/XML-Downloads).
 * Der Access-Token wird im Header mitgesendet (kein <a href> möglich, da der
 * Token nur im Speicher liegt), 401 löst einen einmaligen Refresh aus.
 */
async function requestBlob(path: string, companyId: string, retry = true): Promise<Blob> {
  const headers = new Headers({ 'x-company-id': companyId });
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  const res = await fetch(`${API_URL}${path}`, { headers, credentials: 'include' });
  if (res.status === 401 && retry) {
    const refreshed = await tryRefresh();
    if (refreshed) return requestBlob(path, companyId, false);
  }
  if (!res.ok) return parseError(res);
  return res.blob();
}

/**
 * POST mit echtem Upload-Fortschritt via XMLHttpRequest (fetch kann keine
 * upload.onprogress-Events). onProgress erhaelt gesendete/gesamte Bytes.
 * 401 loest genau einen Refresh + einmaligen Retry aus (wie request()).
 */
function requestWithUpload<T>(
  path: string,
  body: string,
  headers: Record<string, string>,
  onProgress?: (sent: number, total: number) => void,
  retry = true,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/json');
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = async () => {
      if (xhr.status === 401 && retry) {
        const refreshed = await tryRefresh();
        if (refreshed) {
          resolve(await requestWithUpload<T>(path, body, headers, onProgress, false));
          return;
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? (JSON.parse(xhr.responseText) as T) : (undefined as T));
        } catch {
          reject(new Error('Ungueltige Serverantwort.'));
        }
      } else {
        let msg = 'Upload fehlgeschlagen.';
        try {
          const j = JSON.parse(xhr.responseText) as { error?: string };
          if (j.error) msg = j.error;
        } catch {
          /* Roh-Text (z. B. nginx-413) -> generische Meldung behalten. */
        }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload.'));
    xhr.send(body);
  });
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = (await res.json()) as AuthResponse;
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}

export const authApi = {
  async register(email: string, password: string, name?: string, ref?: string): Promise<AuthResponse> {
    const data = await request<AuthResponse>('/auth/register', {
      method: 'POST',
      // acceptTerms: aktive Einwilligung in AGB + Datenschutz (Pflicht im Backend).
      body: JSON.stringify({ email, password, name, acceptTerms: true, ...(ref ? { ref } : {}) }),
    });
    accessToken = data.accessToken;
    return data;
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    const data = await request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    accessToken = data.accessToken;
    return data;
  },

  async logout(): Promise<void> {
    await request<{ ok: boolean }>('/auth/logout', { method: 'POST' }).catch(() => undefined);
    accessToken = null;
  },

  async me(): Promise<AuthUser> {
    const data = await request<{ user: AuthUser }>('/auth/me');
    return data.user;
  },

  /** Fordert einen Passwort-Reset-/Erst-Passwort-Link an (antwortet immer ok). */
  async requestPasswordReset(email: string): Promise<{ devResetLink?: string }> {
    return request<{ ok: true; devResetLink?: string }>('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Setzt das Passwort anhand eines Reset-Tokens. Bestandsnutzer geben
   * zusätzlich ihren persönlichen Aktivierungs-Code an.
   */
  async resetPassword(token: string, password: string, code?: string): Promise<void> {
    await request<{ ok: boolean }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password, code }),
    });
  },

  /** Beim App-Start: versucht eine bestehende Sitzung wiederherzustellen. */
  async bootstrap(): Promise<AuthUser | null> {
    const refreshed = await tryRefresh();
    if (!refreshed) return null;
    try {
      return await this.me();
    } catch {
      return null;
    }
  },
};

// Mandanten-Header fuer company-scoped Endpunkte.
function companyHeader(companyId: string): HeadersInit {
  return { 'x-company-id': companyId };
}

export interface NewCompanyInput {
  name: string;
  chartOfAccounts: 'SKR03' | 'SKR04';
  legalForm?: string | undefined;
  taxNumber?: string | undefined;
  vatId?: string | undefined;
  kleinunternehmer?: boolean | undefined;
  gewerbesteuer?: boolean | undefined;
  ustvaRhythmus?: 'monthly' | 'quarterly' | 'none' | null | undefined;
  industry?: string | undefined;
  street?: string | undefined;
  postalCode?: string | undefined;
  city?: string | undefined;
  /** Bestaetigt das Anlegen trotz bereits vorhandener gleichnamiger Firma. */
  confirmDuplicateName?: boolean | undefined;
}

// ---- Domaenen-Typen ----
export interface Customer {
  id: string;
  customerNumber?: string | null;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country?: string | null;
  vatId: string | null;
  salutation?: string | null;
  contactPerson?: string | null;
  kind?: 'customer' | 'supplier' | 'both';
  discountPercent?: number | string;
  paymentTerms: number;
  notes: string | null;
  category?: string | null;
  industry?: string | null;
  region?: string | null;
  tags?: string | null;
  vip?: boolean;
  consentStatus?: 'none' | 'given' | 'revoked' | null;
  consentDate?: string | null;
  active?: boolean;
}

export interface CustomerStats {
  revenueCents: number;
  openCents: number;
  invoiceCount: number;
  paidCount: number;
  openCount: number;
  overdueCount: number;
  avgPaymentDays: number | null;
  lastInvoiceDate: string | null;
  quoteCount: number;
  expenseCents: number;
  receiptCount: number;
}

export interface CustomerReceiptRow {
  id: string;
  date: string | null;
  direction: string;
  description: string | null;
  vendor: string | null;
  grossCents: number;
  invoiceNumber: string | null;
}

export interface CustomerInvoiceRow {
  id: string;
  type?: 'invoice' | 'quote';
  number: string | null;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  validUntil?: string | null;
  paidAt: string | null;
  grossCents: number;
  netCents: number;
}

export interface InvoiceItem {
  id?: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  discountPercent?: number;
  taxRate: number;
  productId?: string | null;
}

/** Vorschau des Stripe-Rechnungsimports (schreibt nichts). */
export interface StripeImportPreview {
  rechnungen: {
    stripeId: string;
    number: string | null;
    date: string | null;
    stripeCustomerId: string | null;
    netCents: number;
    taxCents: number;
    grossCents: number;
    currency: string;
    status: string;
    bereitsImportiert: boolean;
  }[];
  /** Je Stripe-Kunde eine Gruppe — hier ordnet der Nutzer zu. */
  kundenGruppen: { stripeCustomerId: string; anzahl: number; summeCents: number; customerId: string | null }[];
  /** Auswahlliste der vorhandenen Kontakte. */
  kunden: { id: string; label: string; email: string | null }[];
  neu: number;
  fremdwaehrung: string[];
}

export interface Invoice {
  id: string;
  number: string | null;
  type: 'invoice' | 'quote' | 'recurring';
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'accepted';
  customerId: string | null;
  customer?: { id: string; customerNumber?: string | null; companyName: string | null; firstName: string | null; lastName: string | null; email?: string | null } | null;
  recipientName: string | null;
  recipientAddress?: string | null;
  issueDate: string | null;
  dueDate: string | null;
  validUntil?: string | null;
  acceptedAt?: string | null;
  convertedToId?: string | null;
  netCents: number;
  taxCents: number;
  grossCents: number;
  skontoPercent?: number | string;
  skontoDays?: number | null;
  notes: string | null;
  items?: InvoiceItem[];
}

export interface Receipt {
  id: string;
  direction: 'income' | 'expense';
  date: string;
  vendor: string | null;
  supplierId?: string | null;
  description: string | null;
  invoiceNumber?: string | null;
  grossCents: number;
  netCents: number | null;
  taxRate: string | null;
  category: string | null;
  accountId?: string | null;
  accountNumber?: string | null;
  isBewirtung?: boolean;
  isPrivate?: boolean;
  items?: ReceiptItem2[];
  ocrStatus?: string;
  fileMime?: string | null;
}

/** Moegliche Dublette (vom Backend gemeldet). */
export interface ReceiptDuplicate {
  id: string;
  vendor: string | null;
  invoiceNumber: string | null;
  date: string;
  grossCents: number;
  reason: string;
}

export interface ReceiptItem2 {
  id?: string;
  description: string;
  quantity: number;
  grossCents: number;
  netCents?: number;
  taxRate: number;
  accountNumber?: string | null;
}

export interface TaxDeadlineItem { label: string; date: string; kind: string }
export interface MonthPoint { key: string; label: string; incomeCents: number; expenseCents: number }
export interface DashboardData {
  openInvoices: { count: number; sumCents: number };
  revenueThisMonthCents: number;
  overdueCount: number;
  receiptsThisMonth: number;
  customerCount: number;
  thisMonth: { incomeCents: number; expenseCents: number; resultCents: number };
  thisYear: { year: number; incomeCents: number; expenseCents: number; resultCents: number };
  lastYear: { year: number; incomeCents: number; expenseCents: number; resultCents: number };
  monthly: MonthPoint[];
  // Chart-Reihen je Periode (Jahr/Vorjahr = 12 Monate, Monat = Tage).
  chartYear: MonthPoint[];
  chartLastYear: MonthPoint[];
  chartMonth: MonthPoint[];
  taxDeadlines: TaxDeadlineItem[];
  kleinunternehmer: {
    year: number;
    isKleinunternehmer: boolean;
    laufendUmsatzCents: number;
    grenzeCents: number;
    auslastungProzent: number;
    warnung: boolean;
  };
  ustVorschau: {
    applicable: boolean;
    ustCents: number;
    vorsteuerCents: number;
    zahllastCents: number;
    quarter: number;
    year: number;
  };
  liquidity: {
    startBalanceCents: number;
    weeks: { key: string; label: string; inCents: number; outCents: number; balanceCents: number }[];
  };
  topCustomers: { name: string; revenueCents: number }[];
  customerGrowth: { thisYear: number; lastYear: number; deltaPercent: number };
  avgInvoiceCents: number;
}

export const companyApi = {
  /** Alle Firmen des Nutzers (fuer Mandantenwahl + Onboarding-Check). */
  async list(): Promise<CompanySummary[]> {
    const data = await request<{ companies: CompanySummary[] }>('/companies');
    return data.companies;
  },

  /** Firma anlegen (Onboarding). Legt Owner + SKR-Kontenrahmen an. */
  async create(input: NewCompanyInput): Promise<CompanySummary> {
    const data = await request<{ company: CompanySummary }>('/companies', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data.company;
  },

  /** Plan-/Nutzungsuebersicht der aktiven Firma. */
  async usage(companyId: string): Promise<CompanyUsage> {
    return request<CompanyUsage>(`/companies/${companyId}/usage`, {
      headers: companyHeader(companyId),
    });
  },

  /** Kleinunternehmer-Status fuer ein Jahr (automatisch + ggf. manuell). */
  async kuStatus(companyId: string, year: number): Promise<KuStatus> {
    const d = await request<{ status: KuStatus }>(`/companies/${companyId}/kleinunternehmer?year=${year}`, { headers: companyHeader(companyId) });
    return d.status;
  },
  /** KU-Status fuer ein Jahr manuell setzen (isKleinunternehmer=null -> zurueck auf Automatik). */
  async setKuStatus(companyId: string, year: number, isKleinunternehmer: boolean | null, note?: string): Promise<KuStatus> {
    const d = await request<{ status: KuStatus }>(`/companies/${companyId}/kleinunternehmer`, {
      method: 'PUT', headers: companyHeader(companyId), body: JSON.stringify({ year, isKleinunternehmer, note }),
    });
    return d.status;
  },
  /** AVV-Status (Auftragsverarbeitung Art. 28): aktuelle Fassung akzeptiert? */
  async avvStatus(companyId: string): Promise<AvvStatus> {
    return request<AvvStatus>(`/companies/${companyId}/avv`, { headers: companyHeader(companyId) });
  },
  /** AVV der aktuellen Fassung akzeptieren (owner/admin). */
  async acceptAvv(companyId: string, version: string): Promise<void> {
    await request(`/companies/${companyId}/avv/accept`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ version }),
    });
  },
};

export interface AvvStatus {
  currentVersion: string;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  accepted: boolean;
  canAccept: boolean;
}

export interface InvoiceDesignItem {
  key: string; name: string; tier: 'standard' | 'premium'; accent: string; description: string; locked: boolean;
}
export const invoiceDesignApi = {
  async list(companyId: string): Promise<{ designs: InvoiceDesignItem[]; premiumAllowed: boolean }> {
    return request(`/companies/${companyId}/invoice-designs`, { headers: companyHeader(companyId) });
  },
  /** Authentifizierte Design-Vorschau als Blob (Muster-Rechnung). */
  async preview(companyId: string, design: string, accent?: string): Promise<Blob> {
    const a = accent ? `&accent=${encodeURIComponent(accent)}` : '';
    return requestBlob(`/invoices/design-preview?design=${encodeURIComponent(design)}${a}`, companyId);
  },
};

export interface KuStatus {
  year: number;
  isKleinunternehmer: boolean;
  source: 'auto' | 'manual';
  vorjahrUmsatzCents: number;
  laufendUmsatzCents: number;
  thresholds: { vorjahrCents: number; laufendesJahrCents: number; sofortBeiUeberschreiten: boolean };
  reason: string;
}

// Alle folgenden APIs sind mandanten-scoped: companyId -> x-company-id-Header.
export const customerApi = {
  async list(companyId: string, opts?: { includeInactive?: boolean; kind?: 'customer' | 'supplier' }): Promise<Customer[]> {
    const p = new URLSearchParams();
    if (opts?.includeInactive) p.set('includeInactive', '1');
    if (opts?.kind) p.set('kind', opts.kind);
    const qs = p.toString() ? `?${p.toString()}` : '';
    const d = await request<{ customers: Customer[] }>(`/customers${qs}`, { headers: companyHeader(companyId) });
    return d.customers;
  },
  async create(companyId: string, input: Partial<Customer>): Promise<Customer> {
    const d = await request<{ customer: Customer }>('/customers', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.customer;
  },
  async update(companyId: string, id: string, input: Partial<Customer>): Promise<Customer> {
    const d = await request<{ customer: Customer }>(`/customers/${id}`, {
      method: 'PATCH',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.customer;
  },
  async remove(companyId: string, id: string): Promise<void> {
    await request(`/customers/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  async get(companyId: string, id: string): Promise<Customer> {
    const d = await request<{ customer: Customer }>(`/customers/${id}`, { headers: companyHeader(companyId) });
    return d.customer;
  },
  async stats(companyId: string, id: string): Promise<{ stats: CustomerStats; invoices: CustomerInvoiceRow[]; quotes: CustomerInvoiceRow[]; receipts: CustomerReceiptRow[] }> {
    return request<{ stats: CustomerStats; invoices: CustomerInvoiceRow[]; quotes: CustomerInvoiceRow[]; receipts: CustomerReceiptRow[] }>(
      `/customers/${id}/stats`,
      { headers: companyHeader(companyId) },
    );
  },
  async dsgvoExport(companyId: string, id: string): Promise<Blob> {
    return requestBlob(`/customers/${id}/dsgvo-export`, companyId);
  },
  async purge(companyId: string, id: string): Promise<{ ok: boolean; mode: 'deleted' | 'anonymized'; invoiceCount?: number; receiptCount?: number }> {
    return request(`/customers/${id}/purge`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  async importRows(companyId: string, rows: Partial<Customer>[]): Promise<{ created: number; skipped: number; errors: string[] }> {
    return request<{ created: number; skipped: number; errors: string[] }>('/customers/import', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ rows }),
    });
  },
  async exportCsv(companyId: string): Promise<Blob> {
    return requestBlob('/customers/export', companyId);
  },
};

export interface Product {
  id: string;
  name: string;
  description: string | null;
  sku: string | null;
  unitPriceCents: number;
  purchasePriceCents: number | null;
  taxRate: string; // Decimal als String vom Server
  unit: string;
  category: string | null;
  trackStock: boolean;
  stockQty: number;
  lowStockThreshold: number | null;
  active: boolean;
}

export interface ProductStatRow {
  productId: string;
  name: string;
  qty: number;
  revenueCents: number;
  marginCents: number;
}

export const productApi = {
  async list(companyId: string, opts?: { includeInactive?: boolean }): Promise<Product[]> {
    const qs = opts?.includeInactive ? '?includeInactive=1' : '';
    const d = await request<{ products: Product[] }>(`/products${qs}`, { headers: companyHeader(companyId) });
    return d.products;
  },
  async create(companyId: string, input: Partial<Product>): Promise<Product> {
    const d = await request<{ product: Product }>('/products', {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(input),
    });
    return d.product;
  },
  async update(companyId: string, id: string, input: Partial<Product>): Promise<Product> {
    const d = await request<{ product: Product }>(`/products/${id}`, {
      method: 'PATCH', headers: companyHeader(companyId), body: JSON.stringify(input),
    });
    return d.product;
  },
  async remove(companyId: string, id: string): Promise<void> {
    await request(`/products/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  async stats(companyId: string, year: number): Promise<{ year: number; stats: ProductStatRow[] }> {
    return request(`/products/stats?year=${year}`, { headers: companyHeader(companyId) });
  },
  async adjustStock(companyId: string, id: string, delta: number): Promise<Product> {
    const d = await request<{ product: Product }>(`/products/${id}/stock`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ delta }),
    });
    return d.product;
  },
};

export interface NewInvoiceInput {
  customerId?: string | undefined;
  type?: 'invoice' | 'quote' | 'recurring' | undefined;
  issueDate?: string | undefined; // Rechnungsdatum; leer -> Backend nimmt heute
  dueDate?: string | undefined;
  validUntil?: string | undefined;
  notes?: string | undefined;
  recipientName?: string | undefined;
  recipientAddress?: string | undefined;
  autoCreateCustomer?: boolean | undefined;
  skontoPercent?: number | undefined;
  skontoDays?: number | null | undefined;
  items: InvoiceItem[];
}

/** Ergebnis eines Mailversands an den Kunden (Rechnung/Mahnung). */
export interface MailResult { sent: boolean; reason?: string; to?: string }

export const invoiceApi = {
  async list(companyId: string, params?: { status?: string; type?: string }): Promise<Invoice[]> {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    const d = await request<{ invoices: Invoice[] }>(`/invoices${qs ? `?${qs}` : ''}`, {
      headers: companyHeader(companyId),
    });
    return d.invoices;
  },
  async get(companyId: string, id: string): Promise<Invoice> {
    const d = await request<{ invoice: Invoice }>(`/invoices/${id}`, { headers: companyHeader(companyId) });
    return d.invoice;
  },
  /** Stripe-Export prüfen, ohne zu schreiben (Dubletten + Kundenzuordnung). */
  async importStripePreview(companyId: string, content: string): Promise<StripeImportPreview> {
    return request('/invoices/import/stripe/preview', {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ content }),
    });
  },
  /** Stripe-Rechnungen übernehmen. `zuordnung`: Stripe-Kunden-ID -> Kontakt-ID. */
  async importStripeApply(
    companyId: string,
    content: string,
    zuordnung: Record<string, string>,
    taxRate: number,
  ): Promise<{ angelegt: number; uebersprungen: number; fehler: string[] }> {
    return request('/invoices/import/stripe/apply', {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ content, zuordnung, taxRate }),
    });
  },
  async create(companyId: string, input: NewInvoiceInput): Promise<Invoice> {
    const d = await request<{ invoice: Invoice }>('/invoices', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.invoice;
  },
  /** Entwurf bearbeiten — nur solange status='draft' (sonst 409 NOT_DRAFT). */
  /** Rechnungsberichtigung: nur Textfelder einer versendeten Rechnung (Betraege gesperrt). */
  async correct(companyId: string, id: string, input: { recipientName?: string; recipientAddress?: string; notes?: string | null; items?: { id: string; description: string }[] }): Promise<Invoice> {
    const d = await request<{ invoice: Invoice }>(`/invoices/${id}/correct`, {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.invoice;
  },
  /** Storniert die Rechnung und legt eine bearbeitbare Kopie als Entwurf an. */
  async replace(companyId: string, id: string): Promise<{ invoice: Invoice; cancelledId: string }> {
    return request<{ invoice: Invoice; cancelledId: string }>(`/invoices/${id}/replace`, {
      method: 'POST',
      headers: companyHeader(companyId),
    });
  },
  async update(companyId: string, id: string, input: NewInvoiceInput): Promise<Invoice> {
    const d = await request<{ invoice: Invoice }>(`/invoices/${id}`, {
      method: 'PATCH',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.invoice;
  },
  /** Wie action('send'), liefert zusaetzlich das Ergebnis des Mailversands. */
  async send(companyId: string, id: string, opts?: { sendMail?: boolean; to?: string; message?: string }): Promise<{ invoice: Invoice; mail: MailResult | null }> {
    return request<{ invoice: Invoice; mail: MailResult | null }>(`/invoices/${id}/send`, {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(opts ?? {}),
    });
  },
  async action(companyId: string, id: string, action: 'send' | 'pay' | 'cancel' | 'accept', opts?: { withSkonto?: boolean; sendMail?: boolean; to?: string; message?: string; paidAt?: string }): Promise<Invoice> {
    const d = await request<{ invoice: Invoice }>(`/invoices/${id}/${action}`, {
      method: 'POST',
      headers: companyHeader(companyId),
      ...(opts ? { body: JSON.stringify(opts) } : {}),
    });
    return d.invoice;
  },
  /** Angebot in eine echte Rechnung umwandeln. Liefert die NEUE Rechnung. */
  async convert(companyId: string, id: string): Promise<Invoice> {
    const d = await request<{ invoice: Invoice }>(`/invoices/${id}/convert`, {
      method: 'POST',
      headers: companyHeader(companyId),
    });
    return d.invoice;
  },
  async remove(companyId: string, id: string): Promise<void> {
    await request(`/invoices/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  /** Sichtbares Rechnungs-PDF (DIN 5008). preview=true -> inline anzeigbar. */
  async pdf(companyId: string, id: string, preview = false): Promise<Blob> {
    return requestBlob(`/invoices/${id}/pdf${preview ? '?preview' : ''}`, companyId);
  },
  /** ZUGFeRD/Factur-X Hybrid-PDF (PDF + eingebettetes XML). */
  async zugferd(companyId: string, id: string): Promise<Blob> {
    return requestBlob(`/invoices/${id}/zugferd`, companyId);
  },
  /** XRechnung (reines UBL-XML). */
  async xrechnung(companyId: string, id: string): Promise<Blob> {
    return requestBlob(`/invoices/${id}/xrechnung`, companyId);
  },
};

export interface NewReceiptInput {
  direction: 'income' | 'expense';
  date: string;
  vendor?: string | undefined;
  supplierId?: string | null | undefined;
  description?: string | undefined;
  invoiceNumber?: string | null | undefined;
  grossCents: number;
  taxRate?: number | undefined;
  category?: string | undefined;
  accountNumber?: string | null | undefined;
  isBewirtung?: boolean | undefined;
  isPrivate?: boolean | undefined;
  items?: ReceiptItem2[] | undefined;
}

export const receiptApi = {
  async list(companyId: string): Promise<Receipt[]> {
    const d = await request<{ receipts: Receipt[] }>('/receipts', { headers: companyHeader(companyId) });
    return d.receipts;
  },
  async create(companyId: string, input: NewReceiptInput): Promise<{ receipt: Receipt; duplicates: ReceiptDuplicate[] }> {
    return request<{ receipt: Receipt; duplicates: ReceiptDuplicate[] }>('/receipts', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
  },
  /** Altbestand nachverbuchen: alle Belege mit Konto ohne Buchung ins Journal buchen. */
  async bookAll(companyId: string): Promise<{ booked: number; candidates: number; skipped: number }> {
    return request<{ booked: number; candidates: number; skipped: number }>('/receipts/book-all', {
      method: 'POST',
      headers: companyHeader(companyId),
    });
  },
  /** Vorab-Pruefung auf moegliche Dubletten (vor dem Speichern). */
  async checkDuplicate(companyId: string, input: { invoiceNumber?: string | null | undefined; vendor?: string | null | undefined; grossCents?: number | undefined; date?: string | undefined; excludeId?: string | undefined }): Promise<ReceiptDuplicate[]> {
    const d = await request<{ duplicates: ReceiptDuplicate[] }>('/receipts/check-duplicate', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.duplicates;
  },
  /** Bestaetigte "keine Dublette"-Paare (Beleg-ID-Paare) des Mandanten. */
  async dupOkPairs(companyId: string): Promise<[string, string][]> {
    const d = await request<{ pairs: [string, string][] }>('/receipts/dup-ok', {
      headers: companyHeader(companyId),
    });
    return d.pairs;
  },
  /**
   * "Diese Belege sind keine Dublette" bestaetigen (value=false zieht es
   * zurueck). Paarweise, damit ein spaeter doppelt hochgeladener Beleg weiter
   * geprueft wird. Reiner Anzeige-Hinweis -> eigener Endpunkt ohne
   * GoBD-Jahressperre.
   */
  async setDupOk(companyId: string, receiptIds: string[], value: boolean): Promise<void> {
    await request('/receipts/dup-ok', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ receiptIds, value }),
    });
  },
  async update(companyId: string, id: string, input: Partial<NewReceiptInput>): Promise<Receipt> {
    const d = await request<{ receipt: Receipt }>(`/receipts/${id}`, {
      method: 'PATCH',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.receipt;
  },
  async remove(companyId: string, id: string): Promise<void> {
    await request(`/receipts/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  /** Haendler-Logos fuer die Belegliste. key (normalisiert) -> Data-URL | null. */
  async merchantLogos(companyId: string, merchants: string[]): Promise<Record<string, string | null>> {
    const d = await request<{ logos: Record<string, string | null> }>('/receipts/merchant-logos', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ merchants }),
    });
    return d.logos;
  },
  /** Beleg-Datei (Bild/PDF) als Blob — fuer die Vorschau im Editor. */
  async file(companyId: string, id: string): Promise<Blob> {
    return requestBlob(`/receipts/${id}/file`, companyId);
  },
  /**
   * Batch-Upload (Base64-Bilder/PDFs) -> KI-Belegerkennung im Hintergrund.
   * onProgress meldet den Upload-Fortschritt in Bytes (fuer die Progressbar).
   */
  async upload(
    companyId: string,
    files: { data: string; mime: string }[],
    onProgress?: (sent: number, total: number) => void,
  ): Promise<{ created: number }> {
    return requestWithUpload<{ created: number }>(
      '/receipts/upload',
      JSON.stringify({ files }),
      { 'x-company-id': companyId },
      onProgress,
    );
  },
};

export const dashboardApi = {
  async get(companyId: string): Promise<DashboardData> {
    return request<DashboardData>('/reports/dashboard', { headers: companyHeader(companyId) });
  },
};

// ---- Kennzahlen & KPIs ----
export interface KpiData {
  year: number;
  // Rentabilitaet
  umsatzCents: number;
  umsatzLastYearCents: number;
  yoyProzent: number | null;
  gewinnCents: number;
  ausgabenCents: number;
  umsatzrenditeProzent: number;
  kostenquoteProzent: number | null;
  kostenGruppen: { key: string; label: string; cents: number }[];
  // Forderungen
  offeneForderungenCents: number;
  offeneAnzahl: number;
  aging: { current: number; d30: number; d60: number; d90plus: number };
  avgPaymentDays: number | null;
  onTimeQuote: number | null;
  // Kunden
  topCustomers: { name: string; netCents: number; count: number }[];
  // Trend / Liquiditaet
  monthly: { label: string; incomeCents: number }[];
  liquiditaetCents: number;
  hatBank: boolean;
}

export const kpiApi = {
  async get(companyId: string, year: number): Promise<KpiData> {
    return request<KpiData>(`/reports/kpis?year=${year}`, { headers: companyHeader(companyId) });
  },
};

// ---- Kassenbuch ----
export type CashKind = 'income' | 'expense' | 'privateinlage' | 'privatentnahme';

export interface CashbookEntry {
  id: string;
  date: string;
  documentRef: string | null;
  description: string | null;
  direction: 'income' | 'expense';
  grossCents: number;
  account: { number: string; name: string } | null;
  balanceCents: number;
  isReversal: boolean;
  reversed: boolean;
  /** Vorgangsart — füllt das Bearbeiten-Formular vor. */
  kind: CashKind;
  /** USt-Satz der Buchung (0/7/19). */
  taxRate: number;
}

export interface CashbookData {
  year: number;
  openingCents: number;
  closingCents: number;
  cashAccount: string;
  entries: CashbookEntry[];
}

export interface CashbookEntryInput {
  date: string;
  kind: CashKind;
  grossCents: number;
  taxRate?: number | undefined;
  accountNumber?: string | null | undefined;
  description?: string | undefined;
}

/** Eine vom Foto-Import vorgeschlagene Zeile (noch nicht gebucht). */
export interface CashbookImportRow {
  date: string | null;
  description: string | null;
  direction: 'income' | 'expense';
  grossCents: number | null;
  taxRate: number | null;
  accountNumber: string | null;
  /** Steht mit gleichem Datum und Betrag bereits im Kassenbuch (Server-Abgleich). */
  alreadyBooked?: boolean;
  /** Wird übernommen? Vom Client gesetzt; Dubletten starten abgewählt. */
  selected?: boolean;
}

export const cashbookApi = {
  async list(companyId: string, year: number): Promise<CashbookData> {
    return request<CashbookData>(`/cashbook?year=${year}`, { headers: companyHeader(companyId) });
  },
  async create(companyId: string, input: CashbookEntryInput): Promise<{ id: string }> {
    return request<{ id: string }>('/cashbook', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(input) });
  },
  async setOpening(companyId: string, year: number, openingCents: number): Promise<void> {
    await request('/cashbook/opening', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ year, openingCents }) });
  },
  async reverse(companyId: string, id: string): Promise<void> {
    await request(`/cashbook/${id}/reverse`, { method: 'POST', headers: companyHeader(companyId) });
  },
  /**
   * Buchung ändern. GoBD-konform als Storno + Neubuchung — die alte Fassung
   * bleibt im Journal sichtbar. Liefert die ID der neuen Buchung.
   */
  async update(companyId: string, id: string, input: CashbookEntryInput): Promise<{ id: string }> {
    return request(`/cashbook/${id}`, {
      method: 'PATCH',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
  },
  /**
   * Auswertung anstossen (Foto, PDF oder Tabelle). Laeuft im Hintergrund —
   * liefert nur die Auftragsnummer, das Ergebnis holt importStatus().
   */
  async importPhotos(
    companyId: string,
    files: { data: string; mime: string }[],
  ): Promise<{ jobId: string }> {
    return request('/cashbook/import', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ files }) });
  },
  /** Stand eines Import-Auftrags. */
  async importStatus(
    companyId: string,
    jobId: string,
  ): Promise<{
    status: 'queued' | 'running' | 'done' | 'failed';
    total: number;
    done: number;
    entries: CashbookImportRow[];
    error: string | null;
    warnings: string[];
  }> {
    return request(`/cashbook/import/${jobId}`, { headers: companyHeader(companyId) });
  },
  async confirmImport(companyId: string, entries: CashbookEntryInput[]): Promise<{ booked: number; errors: { index: number; error: string }[] }> {
    return request<{ booked: number; errors: { index: number; error: string }[] }>('/cashbook/import/confirm', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ entries }) });
  },
};

export interface Account {
  id: string;
  number: string;
  name: string;
  type: string;
}

export interface JournalLine {
  id?: string;
  accountId: string;
  debitCents: number;
  creditCents: number;
  account?: { number: string; name: string };
}

export interface JournalEntry {
  id: string;
  date: string;
  type: 'general' | 'cash';
  description: string | null;
  documentRef: string | null;
  lines: JournalLine[];
}

export interface SusaRow {
  number: string;
  name: string;
  type: string;
  ebCents: number;
  debitCents: number;
  creditCents: number;
  saldoCents: number;
}
export interface Susa {
  rows: SusaRow[];
  sumDebitCents: number;
  sumCreditCents: number;
  sumSaldoCents: number;
  balanced: boolean;
}

export interface AccountMapping {
  number: string;
  action: 'create' | 'map' | 'closing' | 'skip';
  targetNumber: string | null;
  name: string | null;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | null;
  reason: string;
  confidence: number;
  count?: number;
}

export interface ClosingArtifact {
  id: string;
  date: string;
  vendor: string | null;
  accountNumber: string | null;
  grossCents: number;
  booked: boolean;
}

export interface OrphanEntry {
  id: string;
  date: string;
  description: string | null;
  debitCents: number;
}

export interface ImportEuerYear {
  year: number;
  einnahmenCents: number;
  ausgabenCents: number;
  ueberschussCents: number;
  positionen: { label: string; cents: number }[];
}

export interface ImportPreview {
  format: string;
  totalRows: number;
  counts: { expenses: number; incomes: number; assets: number; journals: number; stornosNetted: number; skipped: number };
  newAccounts: { number: string; name: string; type: string; source: 'skr' | 'ai' | 'heuristic' }[];
  sampleExpenses: { date: string; text: string; account: string; grossCents: number }[];
  sampleIncomes: { date: string; text: string; account: string; grossCents: number }[];
  warnings: string[];
  euerByYear: ImportEuerYear[];
}

export interface ImportResult {
  createdAccounts: number;
  createdReceipts: number;
  createdInvoices: number;
  createdAssets: number;
  createdJournalEntries: number;
  skipped: number;
  alreadyImported: number;
}

/** Konkreter Verursacher einer fehlgeschlagenen Prüfung (zum Hinspringen). */
export interface ChecklistRef {
  id: string;
  target: 'receipt' | 'invoice' | 'asset' | 'journal';
  label: string;
  hint?: string;
  amountCents?: number;
  date?: string;
}

export interface ChecklistItem {
  key: string;
  label: string;
  ok: boolean;
  severity: 'error' | 'warn' | 'info';
  detail: string;
  refs?: ChecklistRef[];
  refsTruncated?: boolean;
}
export interface YearClosingChecklist {
  year: number;
  alreadyClosed: boolean;
  closedAt: string | null;
  canClose: boolean;
  checks: ChecklistItem[];
  gewinnCents: number;
  einnahmenCents: number;
  ausgabenCents: number;
}

export interface Asset {
  id: string;
  name: string;
  purchaseDate: string;
  costCents: number;
  usefulLifeYears: number;
  method: string;
  accountNumber?: string | null;
  source?: string;
  receiptId?: string | null;
  /** Abgang: verkauft, entsorgt oder privat entnommen. */
  disposalDate?: string | null;
  disposalProceedsCents?: number;
  /** Sonderabschreibung § 7g Abs. 5/6 EStG. */
  sonderAfaCents?: number;
  depreciation?: { year: number; amountCents: number; bookValueCents: number }[];
}

export const accountingApi = {
  async accounts(companyId: string): Promise<Account[]> {
    const d = await request<{ accounts: Account[] }>('/accounts', { headers: companyHeader(companyId) });
    return d.accounts;
  },
  async journal(companyId: string, filter?: { year?: number; month?: number }): Promise<JournalEntry[]> {
    const qs = new URLSearchParams();
    if (filter?.year) qs.set('year', String(filter.year));
    if (filter?.month) qs.set('month', String(filter.month));
    const suffix = qs.toString() ? `?${qs}` : '';
    const d = await request<{ entries: JournalEntry[] }>(`/journal${suffix}`, { headers: companyHeader(companyId) });
    return d.entries;
  },
  async addEntry(
    companyId: string,
    input: { date: string; type: 'general' | 'cash'; description?: string | undefined; lines: { accountId: string; debitCents: number; creditCents: number }[] },
  ): Promise<JournalEntry> {
    const d = await request<{ entry: JournalEntry }>('/journal', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.entry;
  },
  async reverse(companyId: string, id: string): Promise<void> {
    await request(`/journal/${id}/reverse`, { method: 'POST', headers: companyHeader(companyId) });
  },
  /** Summen- und Saldenliste (DATEV) fuer ein Jahr. */
  async susa(companyId: string, year: number): Promise<{ year: number; susa: Susa }> {
    return request<{ year: number; susa: Susa }>(`/reports/susa?year=${year}`, { headers: companyHeader(companyId) });
  },
  /** SuSa als PDF (Download). */
  async susaPdf(companyId: string, year: number): Promise<Blob> {
    return requestBlob(`/reports/susa?year=${year}&format=pdf`, companyId);
  },
  /** Jahresabschluss-Checkliste fuer ein Jahr. */
  async yearClosing(companyId: string, year: number): Promise<YearClosingChecklist> {
    return request<YearClosingChecklist>(`/year-closing/${year}`, { headers: companyHeader(companyId) });
  },
  /** Jahr abschliessen (Festschreibung). */
  async closeYear(companyId: string, year: number): Promise<{ year: number; status: string; closedAt: string }> {
    return request(`/year-closing/${year}/close`, { method: 'POST', headers: companyHeader(companyId) });
  },
  /** Jahresabschluss aufheben. */
  async reopenYear(companyId: string, year: number): Promise<{ year: number; status: string }> {
    return request(`/year-closing/${year}/reopen`, { method: 'POST', headers: companyHeader(companyId) });
  },
  /** Alt-Import: KI-Vorschlag fuer fehlende Konten (read-only). */
  async accountMappingSuggestion(companyId: string): Promise<{ suggestions: AccountMapping[] }> {
    return request<{ suggestions: AccountMapping[] }>('/accounts/mapping-suggestion', { headers: companyHeader(companyId) });
  },
  /** Alt-Import: Mapping anwenden (Konten anlegen, Belege umleiten, buchen). */
  async applyAccountMapping(companyId: string, mappings: AccountMapping[]): Promise<{ createdAccounts: number; redirectedReceipts: number; booked: number; closingReversed?: number; closingCount: number; skipCount: number }> {
    return request('/accounts/apply-mapping', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ mappings }) });
  },
  /** Alt-Import: Abschluss-Artefakte (GWG-Auflösung, EB-Wert) finden (read-only). */
  async closingArtifacts(companyId: string): Promise<{ artifacts: ClosingArtifact[] }> {
    return request<{ artifacts: ClosingArtifact[] }>('/accounts/closing-artifacts', { headers: companyHeader(companyId) });
  },
  /** Alt-Import: Abschluss-Artefakte auflösen (Fehlbuchung stornieren + Konto entfernen). */
  async resolveClosing(companyId: string, ids: string[]): Promise<{ reversed: number; cleared: number }> {
    return request('/accounts/resolve-closing', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ ids }) });
  },
  /** Konsistenz: verwaiste Beleg-Buchungen (ohne Beleg, nicht storniert) finden. */
  async orphanEntries(companyId: string): Promise<{ orphans: OrphanEntry[] }> {
    return request<{ orphans: OrphanEntry[] }>('/accounts/orphan-entries', { headers: companyHeader(companyId) });
  },
  /** Konsistenz: verwaiste Buchungen GoBD-konform stornieren. */
  async resolveOrphans(companyId: string, ids: string[]): Promise<{ reversed: number }> {
    return request('/accounts/resolve-orphans', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ ids }) });
  },
  /** Universal-Import: Datei (base64) analysieren -> Vorschau (kein Schreiben). */
  async importPreview(companyId: string, data: string, filename?: string): Promise<{ preview: ImportPreview }> {
    return request('/import/preview', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ data, filename }) });
  },
  /** Universal-Import: Datei (base64) importieren (schreibt, idempotent). */
  async importApply(companyId: string, data: string, filename?: string): Promise<{ result: ImportResult; importTag: string }> {
    return request('/import/apply', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ data, filename }) });
  },
  async assets(companyId: string): Promise<Asset[]> {
    const d = await request<{ assets: Asset[] }>('/assets', { headers: companyHeader(companyId) });
    return d.assets;
  },
  async addAsset(
    companyId: string,
    input: { name: string; purchaseDate: string; costCents: number; usefulLifeYears: number; method?: 'linear' | 'gwg'; receiptId?: string },
  ): Promise<Asset> {
    const d = await request<{ asset: Asset }>('/assets', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.asset;
  },
  /** Ausgabe-Belege, die fuer eine Anlage verknuepft werden koennen. */
  async linkableReceipts(companyId: string): Promise<{ id: string; date: string; vendor: string | null; description: string | null; grossCents: number; netCents: number | null }[]> {
    const d = await request<{ receipts: { id: string; date: string; vendor: string | null; description: string | null; grossCents: number; netCents: number | null }[] }>('/assets/linkable-receipts', { headers: companyHeader(companyId) });
    return d.receipts;
  },
  async updateAsset(
    companyId: string,
    id: string,
    input: {
      name?: string;
      usefulLifeYears?: number;
      method?: string;
      accountNumber?: string;
      sonderAfaCents?: number;
      disposalDate?: string | null;
      disposalProceedsCents?: number;
    },
  ): Promise<Asset> {
    const d = await request<{ asset: Asset }>(`/assets/${id}`, {
      method: 'PATCH',
      headers: companyHeader(companyId),
      body: JSON.stringify(input),
    });
    return d.asset;
  },
  async removeAsset(companyId: string, id: string): Promise<void> {
    await request(`/assets/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  async afaRunPreview(companyId: string, year: number): Promise<AfaRunPreview> {
    return request<AfaRunPreview>(`/assets/afa-run?year=${year}`, { headers: companyHeader(companyId) });
  },
  async afaRun(companyId: string, year: number): Promise<AfaRunResult> {
    return request<AfaRunResult>(`/assets/afa-run`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ year }) });
  },
  async afaReverse(companyId: string, year: number): Promise<{ year: number; reversed: number; totalCents: number }> {
    return request(`/assets/afa-run/reverse`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ year }) });
  },
};

export interface AfaRunPreview {
  year: number;
  rows: { assetId: string; name: string; amountCents: number; accountNumber: string | null; alreadyPosted: boolean }[];
  openCents: number;
  openCount: number;
}
export interface AfaRunResult {
  year: number;
  posted: number;
  totalCents: number;
  skipped: { name: string; reason: string }[];
}

export interface UstvaResult {
  period: string;
  umsatzNetCents: number;
  umsatzsteuerCents: number;
  vorsteuerCents: number;
  zahllastCents: number;
  /** Namen weiterer Betriebe, deren Umsaetze mitgemeldet werden (§ 2 UStG). */
  zusammengefasst?: string[];
}
export interface EuerResult {
  year: number;
  einnahmenCents: number;
  ausgabenCents: number;
  ueberschussCents: number;
  ausgabenNachKategorie: Record<string, number>;
}
export interface BwaResult {
  year: number;
  erloeseCents: number;
  kostenCents: number;
  ergebnisCents: number;
  margeProzent: number;
}
export interface UstJahrResult {
  year: number;
  umsatzNetCents: number;
  umsatzsteuerCents: number;
  vorsteuerCents: number;
  jahresZahllastCents?: number;
  vorauszahlungenCents?: number;
  zahllastCents: number;
}
export interface EstResult {
  year: number;
  gewinnCents: number;
  zveCents: number;
  estCents: number;
  durchschnittssatzProzent: number;
  schaetzung: boolean;
}
export interface GewStResult {
  year: number;
  hebesatzProzent: number;
  hebesatzQuelle: 'manuell' | 'gemeinde' | 'standard';
  hebesatzCity: string | null;
  freibetragAngewendet: boolean;
  gewinnCents: number;
  gewerbeertragCents: number;
  freibetragCents: number;
  messbetragCents: number;
  steuerCents: number;
  pflichtig: boolean;
  schaetzung: boolean;
}

export const taxApi = {
  async ustva(companyId: string, period: string): Promise<UstvaResult> {
    return request<UstvaResult>(`/taxes/ustva?period=${encodeURIComponent(period)}`, { headers: companyHeader(companyId) });
  },
  async euer(companyId: string, year: number): Promise<EuerResult> {
    return request<EuerResult>(`/reports/euer?year=${year}`, { headers: companyHeader(companyId) });
  },
  async bwa(companyId: string, year: number): Promise<BwaResult> {
    return request<BwaResult>(`/reports/bwa?year=${year}`, { headers: companyHeader(companyId) });
  },
  async euerPdf(companyId: string, year: number): Promise<Blob> {
    return requestBlob(`/reports/euer?year=${year}&format=pdf`, companyId);
  },
  async bwaPdf(companyId: string, year: number): Promise<Blob> {
    return requestBlob(`/reports/bwa?year=${year}&format=pdf`, companyId);
  },
  async ustJahr(companyId: string, year: number): Promise<UstJahrResult> {
    return request<UstJahrResult>(`/taxes/umsatzsteuer-jahr?year=${year}`, { headers: companyHeader(companyId) });
  },
  async est(companyId: string, year: number): Promise<EstResult> {
    return request<EstResult>(`/taxes/einkommensteuer?year=${year}`, { headers: companyHeader(companyId) });
  },
  async gewst(companyId: string, year: number, hebesatz?: number): Promise<GewStResult> {
    const q = hebesatz ? `&hebesatz=${hebesatz}` : '';
    return request<GewStResult>(`/taxes/gewerbesteuer?year=${year}${q}`, { headers: companyHeader(companyId) });
  },
  async setHebesatz(companyId: string, hebesatz: number): Promise<{ ok: boolean; city: string; hebesatz: number }> {
    return request(`/taxes/gewerbesteuer/hebesatz`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ hebesatz }) });
  },

  // --- ELSTER: Steuererklaerung speichern/validieren/uebermitteln ---
  async saveReturn(
    companyId: string,
    type: 'ustva' | 'ustJahr' | 'est' | 'gewst' | 'euer',
    period: string,
    data?: Record<string, unknown>,
  ): Promise<{ entry: TaxReturnEntry }> {
    return request(`/taxes/returns`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ type, period, data }),
    });
  },
  async listReturns(companyId: string): Promise<TaxReturnEntry[]> {
    const d = await request<{ entries: TaxReturnEntry[] }>(`/taxes/returns`, { headers: companyHeader(companyId) });
    return d.entries;
  },
  async validateReturn(companyId: string, id: string): Promise<{ ok: boolean; errors: { code?: string; message: string }[] }> {
    return request(`/taxes/returns/${id}/validate`, { method: 'POST', headers: companyHeader(companyId) });
  },
  /** Vorschau: was genau geht ans Finanzamt (lesbar aufbereitet + Roh-XML). */
  async previewReturn(companyId: string, id: string): Promise<ElsterPreview> {
    return request(`/taxes/returns/${id}/preview`, { headers: companyHeader(companyId) });
  },
  async submitReturn(
    companyId: string, id: string, certificateId: string, pin: string,
  ): Promise<{ ok: boolean; entry: TaxReturnEntry; transferTicket?: string; errors: { message: string }[] }> {
    return request(`/taxes/returns/${id}/submit`, {
      method: 'POST', headers: companyHeader(companyId),
      body: JSON.stringify({ confirmCorrectness: true, certificateId, pin }),
    });
  },
  // ESt-Wizard
  async estWizard(companyId: string, year: number): Promise<{ catalog: WizardCatalog; prefill: Record<string, number | string> }> {
    return request(`/taxes/wizard/est?year=${year}`, { headers: companyHeader(companyId) });
  },
  /** Adressvorschläge für die Arbeitsstätte (gleiche Quelle wie Nachweise). */
  async wizardAddressSuggest(companyId: string, q: string): Promise<string[]> {
    const d = await request<{ results: { label: string }[] }>(
      `/taxes/wizard/address-suggest?q=${encodeURIComponent(q)}`,
      { headers: companyHeader(companyId) },
    );
    return d.results.map((r) => r.label);
  },
  /**
   * Einfache Entfernung Wohnung -> Arbeitsstätte in vollen Kilometern
   * (abgerundet, wie es die Entfernungspauschale verlangt). Die Wohnadresse
   * zieht der Server aus den Firmenstammdaten.
   */
  async wizardEntfernung(
    companyId: string,
    ziel: string,
  ): Promise<{ distanceKm: number; exaktKm: number; vonAufgeloest: string; zielAufgeloest: string }> {
    return request(`/taxes/wizard/entfernung?ziel=${encodeURIComponent(ziel)}`, {
      headers: companyHeader(companyId),
    });
  },
  /**
   * Betriebskostenabrechnung auswerten. Mehrere Dateien werden als Seiten
   * EINER Abrechnung behandelt und gemeinsam ausgewertet.
   */
  async analyzeBetriebskosten(
    companyId: string, files: { data: string; mime: string }[],
  ): Promise<Hnk35aResult> {
    return request(`/taxes/wizard/hnk35a`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ files }),
    });
  },
  /** Steuerprognose aus den Assistenten-Angaben (wie ELSTER nach dem Ausfüllen). */
  async estPrognose(companyId: string, year: number, answers: Record<string, unknown>): Promise<EstPrognose> {
    return request('/taxes/wizard/prognose', {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ year, answers }),
    });
  },
  /** Vorausgefüllte Steuererklärung beim Finanzamt abrufen (eigenes Zertifikat). */
  async fetchVast(
    companyId: string, year: number, certificateId: string, pin: string, idnr?: string,
  ): Promise<VastResult> {
    return request(`/taxes/wizard/vast`, {
      method: 'POST', headers: companyHeader(companyId),
      body: JSON.stringify({ year, certificateId, pin, ...(idnr ? { idnr } : {}) }),
    });
  },
  /** Arztrechnung/Rezept auswerten (außergewöhnliche Belastungen, § 33 EStG). */
  async analyzeKrankheitskosten(companyId: string, base64: string, mime: string): Promise<KrankheitsResult> {
    return request(`/taxes/wizard/krankheitskosten`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ data: base64, mime }),
    });
  },
  /**
   * Krypto-Reports auswerten (Börsen-Journale, Steuerbescheinigungen).
   *
   * Läuft im Hintergrund und liefert nur die Auftragsnummer — ein Jahresjournal
   * braucht länger als das Zeitlimit des Proxys. Das Ergebnis holt
   * kryptoStatus().
   *
   * `vorherJobId` setzt auf einer früheren Auswertung auf: die Haltefrist des
   * § 23 EStG braucht die vollständige Historie (ein Verkauf 2025 kann einen
   * Kauf aus 2023 verbrauchen). Ohne die Angabe würde ein nachgereichtes
   * Journal die bereits bekannten Käufe nicht kennen.
   */
  async analyzeKrypto(
    companyId: string,
    files: { data: string; mime: string; name?: string }[],
    jahr: number,
    vorherJobId?: string,
  ): Promise<{ jobId: string }> {
    return request(`/taxes/wizard/krypto`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ files, jahr, vorherJobId }),
    });
  },
  /** Stand einer Krypto-Auswertung. */
  async kryptoStatus(companyId: string, jobId: string): Promise<KryptoJobStatus> {
    return request(`/taxes/wizard/krypto/${jobId}`, { headers: companyHeader(companyId) });
  },
  /**
   * Bescheide und Mitteilungen aus dem ELSTER-Postfach abholen.
   *
   * Braucht Zertifikat + PIN wie eine Übermittlung — es ist der Eigenabruf
   * mit dem Zertifikat des Nutzers. Abgeholte Bescheide werden gespeichert:
   * ELSTER nimmt quittierte Bereitstellungen aus dem Postfach.
   */
  async fetchNotices(
    companyId: string,
    certificateId: string,
    pin: string,
    umfang: 'neue' | 'alle' = 'neue',
  ): Promise<TaxNoticeFetchResult> {
    return request(`/taxes/notices/fetch`, {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ certificateId, pin, umfang }),
    });
  },
  /** Bereits abgeholte Bescheide auflisten. */
  async notices(companyId: string): Promise<TaxNotice[]> {
    const d = await request<{ notices: TaxNotice[] }>(`/taxes/notices`, { headers: companyHeader(companyId) });
    return d.notices;
  },
  /** Amtliche PDF-Darstellung eines Bescheids herunterladen. */
  async noticePdf(companyId: string, noticeId: string): Promise<Blob> {
    return requestBlob(`/taxes/notices/${noticeId}/pdf`, companyId);
  },
  // Versandhistorie (optional je Steuerart)
  async history(companyId: string, type?: string): Promise<TaxHistoryEntry[]> {
    const q = type ? `?type=${encodeURIComponent(type)}` : '';
    const d = await request<{ history: TaxHistoryEntry[] }>(`/taxes/history${q}`, { headers: companyHeader(companyId) });
    return d.history;
  },
  // Übermittlungsprotokoll-PDF herunterladen
  async protocolPdf(companyId: string, id: string): Promise<Blob> {
    return requestBlob(`/taxes/returns/${id}/protocol`, companyId);
  },
};

/** Ein aus dem ELSTER-Postfach abgeholter Bescheid. */
export interface TaxNotice {
  id: string;
  /** Datenart laut Postfach, z. B. "ESB". */
  datenart: string;
  /** Lesbare Bezeichnung, z. B. "Einkommensteuerbescheid". */
  label: string;
  year: number | null;
  taxOffice: string | null;
  /** Erstelldatum laut Finanzamt (nicht der Abrufzeitpunkt). */
  issuedAt: string | null;
  fetchedAt: string;
  meta: Record<string, string> | null;
  /** Liegt eine amtliche PDF-Darstellung vor? */
  hasPdf: boolean;
}

export interface TaxNoticeFetchResult {
  ok: boolean;
  /** Neu gespeicherte Bescheide (bereits vorhandene zählen nicht mit). */
  neu: number;
  gesamt: number;
  status: { datenart: string; label: string; anzahl: number }[];
  errors: { code?: string; message: string }[];
}

export interface TaxHistoryError {
  supportRef?: string;
  returnCode?: number;
  errors?: { message: string }[];
}
export interface TaxHistoryEntry {
  id: string;
  type: string;
  period: string;
  status: string; // submitting|submitted|accepted|rejected
  transferTicket: string | null;
  submittedAt: string | null;
  confirmedAt: string | null;
  hasProtocol: boolean;
  error: TaxHistoryError | null;
}

/** Lesbare Vorschau des zu uebermittelnden ELSTER-Datensatzes. */
export interface ElsterPreview {
  datenart: string;
  period: string;
  sections: {
    title: string;
    rows: {
      label: string;
      value: string;
      indent?: boolean;
      /** SKR-Konten hinter der Position — für den Abgleich mit der SuSa. */
      accounts?: { number: string; name?: string; value: string }[];
    }[];
  }[];
  xml: string;
}

export interface TaxReturnEntry {
  id: string;
  type: string;
  period: string;
  status: string; // draft|validated|submitting|submitted|accepted|rejected
  elsterTransferId: string | null;
  submittedAt: string | null;
  errorText?: string | null;
  protocolFileKey?: string | null;
  /** Erfasste Wizard-Antworten (Entwurf) — zum Weiterarbeiten. */
  data?: unknown;
}
export interface WizardField {
  // 'moneyEur' = Betrag in vollen Euro (Schema-Typ Ganzzahl), 'money' = mit Cent.
  key: string; label: string; type: 'text' | 'money' | 'moneyEur' | 'number' | 'bool' | 'check' | 'date' | 'select';
  /** Zeile im amtlichen Vordruck (z.B. "16") — Orientierungshilfe. */
  zeile?: string;
  help?: string; required?: boolean; prefillFrom?: string; showIf?: string;
  options?: { value: string; label: string }[];
}
export interface WizardStep {
  key: string; title: string; description?: string; anlage?: string; kurz?: string; fields: WizardField[];
  /** Nur zeigen, wenn diese Einkunftsart zu Beginn ausgewählt wurde. */
  optionalKey?: string;
  /** Je Eintrag eine eigene Anlage (mehrere Kinder / Mietobjekte). */
  repeatable?: { addLabel: string; itemLabel: string; max: number; repeatAt: string };
}
export interface WizardCatalog {
  datenart: string; year: number; steps: WizardStep[];
  auswahl: { key: string; label: string; hint: string }[];
}
export interface Hnk35aPosition {
  bezeichnung: string; kategorie: string; arbeitsanteilCents: number; gesamtCents: number | null;
  begruendung: string; confidence: number;
}
export interface Hnk35aResult {
  positionen: Hnk35aPosition[]; summeHaushaltsnahCents: number; summeHandwerkerCents: number;
  confidence: number; hinweise: string[];
}

/** Ein ausgewerteter Wert aus einem VaSt-Beleg. */
export interface EstPrognose {
  year: number;
  posten: { label: string; cents: number; abzug?: boolean; hint?: string }[];
  einkuenfteCents: number;
  zveCents: number;
  tariflicheSteuerCents: number;
  ermaessigung35aCents: number;
  festzusetzendeSteuerCents: number;
  anrechnungCents: number;
  erstattungCents: number;
  splitting: boolean;
  hinweise: string[];
}

export interface VastWert {
  key?: string;
  label: string;
  cents?: number;
  text?: string;
  /** Klartext, falls `text` ein Schluessel ist ("03" -> "Evangelisch"). */
  anzeige?: string;
}
export interface VastBelegAuswertung {
  belegart: string;
  bezeichnung: string;
  werte: VastWert[];
}
export interface VastResult {
  ok: boolean;
  errors: { code?: string; message: string }[];
  belege: VastBelegAuswertung[];
}

/** Position einer Arztrechnung/eines Rezepts (§ 33 EStG). */
export interface KrankheitsPosition {
  bezeichnung: string;
  kategorie: 'arzt_zahnarzt' | 'medikament_rezept' | 'hilfsmittel' | 'krankenhaus_kur' | 'fahrtkosten' | 'nicht_beguenstigt';
  betragCents: number;
  erstattungCents: number;
  begruendung: string;
  confidence: number;
}
export interface KrankheitsResult {
  positionen: KrankheitsPosition[];
  summeAufwendungenCents: number;
  summeErstattungenCents: number;
  datum: string | null;
  confidence: number;
  hinweise: string[];
}

/** Ein abgeschlossenes Veräußerungsgeschäft (§ 23 EStG), FIFO ermittelt. */
export interface KryptoVeraeusserung {
  asset: string;
  menge: number;
  kaufDatum: string;
  verkaufDatum: string;
  anschaffungCents: number;
  erloesCents: number;
  gewinnCents: number;
  haltedauerTage: number;
  /** Haltedauer über 1 Jahr -> § 23 Abs. 1 Nr. 2 EStG steuerfrei. */
  steuerfrei: boolean;
}

export interface KryptoResult {
  jahr: number;
  veraeusserungen: KryptoVeraeusserung[];
  /** § 23 EStG -> Anlage SO */
  gewinnCents: number;
  verlustCents: number;
  saldoCents: number;
  steuerfreiCents: number;
  unterFreigrenze: boolean;
  /** § 22 Nr. 3 EStG (Staking/Lending) -> Anlage SO */
  stakingCents: number;
  stakingUnterFreigrenze: boolean;
  /** § 20 EStG -> Anlage KAP */
  kapitalertraegeCents: number;
  /** Termingeschäfte (Futures/CFDs/Hebelprodukte), § 20 Abs. 2 Nr. 3 EStG. */
  terminSaldoCents: number;
  terminVerlustCents: number;
  quelle: string | null;
  transaktionen: number;
  confidence: number;
  warnungen: string[];
  hinweise: string[];
}

/** Stand einer im Hintergrund laufenden Krypto-Auswertung. */
export interface KryptoJobStatus {
  status: 'queued' | 'running' | 'done' | 'failed';
  /** Anzahl Dateien insgesamt bzw. bereits ausgewertet — für die Statuszeile. */
  total: number;
  done: number;
  /** Auftragsnummer; beim nächsten Upload als `vorherJobId` mitgeben. */
  jobId: string;
  ergebnis: KryptoResult | null;
  /** Namen aller bisher ausgewerteten Journale. */
  dateien: string[];
  error: string | null;
  /** Dateien, die nicht gelesen werden konnten. */
  warnings: string[];
}

// --- ELSTER-Zertifikate ---
export interface ElsterCertificate {
  id: string; label: string; subject: string | null; validUntil: string | null; pinStored: boolean; createdAt: string;
}
export const elsterApi = {
  async listCertificates(companyId: string): Promise<{ certificates: ElsterCertificate[]; configured: boolean }> {
    return request(`/elster/certificates`, { headers: companyHeader(companyId) });
  },
  async uploadCertificate(
    companyId: string, label: string, base64: string, pin?: string, storePin?: boolean,
  ): Promise<{ certificate: ElsterCertificate }> {
    return request(`/elster/certificates`, {
      method: 'POST', headers: companyHeader(companyId),
      body: JSON.stringify({ label, data: base64, pin, storePin }),
    });
  },
  async deleteCertificate(companyId: string, id: string): Promise<{ ok: boolean }> {
    return request(`/elster/certificates/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  // ELSTER-Lizenzvereinbarung
  async license(companyId: string): Promise<{ license: ElsterLicense; accepted: boolean; currentVersion: string }> {
    return request(`/elster/license`, { headers: companyHeader(companyId) });
  },
  async acceptLicense(companyId: string): Promise<{ ok: boolean; version: string }> {
    return request(`/elster/license/accept`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ accept: true }) });
  },
  // Voraussetzungs-Check (Steuernummer, Adresse, Zertifikat, Lizenz, Plan)
  async readiness(companyId: string): Promise<ElsterReadiness> {
    return request(`/elster/readiness`, { headers: companyHeader(companyId) });
  },
  // Steuer-Uebersicht: Pflichten/Status + Prognosen (amtliche Formeln)
  async overview(companyId: string, year: number): Promise<TaxOverview> {
    return request(`/taxes/overview?year=${year}`, { headers: companyHeader(companyId) });
  },
  // Status manuell setzen (extern eingereicht / wieder offen)
  async setManualStatus(
    companyId: string, type: string, period: string, status: 'extern_eingereicht' | 'offen',
  ): Promise<{ ok: boolean }> {
    return request(`/taxes/status`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ type, period, status }),
    });
  },
};

export interface TaxFristUstva {
  period: string; label: string; dueDate: string;
  state: 'eingereicht' | 'ueberfaellig' | 'offen';
}
export interface TaxFristJahr {
  type: string; label: string; dueDate: string;
  state: 'eingereicht' | 'in_arbeit' | 'offen';
}

export interface TaxPflicht {
  type: string; label: string; pflicht: boolean; rhythmus: string;
  state: 'eingereicht' | 'in_arbeit' | 'offen'; extern: boolean; count: number; lastSubmittedAt: string | null;
}
export interface TaxOverview {
  year: number;
  isKleinunternehmer: boolean;
  isGewerbe: boolean;
  ustvaRhythmus: 'monthly' | 'quarterly' | 'none' | null;
  ustvaRhythmusFehlt: boolean;
  pflichten: TaxPflicht[];
  fristen: { ustva: TaxFristUstva[]; jahres: TaxFristJahr[] };
  verspaetung: { ustvaCents: number; anzahlUeberfaellig: number };
  prognosen: {
    gewinnCents: number;
    ustZahllastCents: number;
    est: { steuerCents: number; durchschnittssatzProzent: number };
    gewerbesteuer: { gewerbeertragCents: number; freibetragCents: number; messbetragCents: number; steuerCents: number; hebesatzProzent: number | null } | null;
    ruecklage: { empfohlenCents: number; prozentVomGewinn: number };
  };
}

export interface ElsterReadinessIssue {
  code: string;
  message: string;
  action?: string;
  link?: string;
}
export interface ElsterReadiness {
  ready: boolean;
  issues: ElsterReadinessIssue[];
}

export interface ElsterLicenseSection { heading: string; paragraphs: string[]; bullets: string[] }
export interface ElsterLicense {
  version: string; title: string; intro: string;
  sections: ElsterLicenseSection[]; confirmations: string[];
}

export interface BankAccount { id: string; name: string; iban: string | null; balanceCents: number; provider: string; lastSyncAt: string | null; connected?: boolean }
export interface StatementImportResult { status: 'done'; accountId: string; imported: number; total: number; duplicates: number; balanceOk: boolean | null; statementNo: string | null }
/** Ergebnis eines Sync-/TAN-Schritts: fertig ODER TAN noetig (FinTS PSD2). */
export type BankSyncResult =
  | { status: 'done'; imported: number; total: number; accounts?: number }
  | { status: 'tan_required'; sessionId: string; decoupled: boolean; challenge: string | null; photo: { mimeType: string; dataUrl: string } | null };
export interface BankTransaction { id: string; date: string; amountCents: number; purpose: string | null; counterparty: string | null; matchStatus: string; matchedInvoiceId: string | null; matchedReceiptId?: string | null; privateJournalEntryId?: string | null }
export interface MatchSuggestion { kind: 'invoice' | 'receipt'; id: string; label: string; amountCents: number; confidence: 'high' | 'medium'; reason: string }
export interface OverdueInvoice { id: string; number: string | null; grossCents: number; dueDate: string | null; recipientName: string | null; customer?: { companyName: string | null; firstName: string | null; lastName: string | null; email?: string | null } | null; dunnings: { level: number; sentAt?: string | null }[] }
export interface TaxForm { id: string; type: string; status: string; payload: Record<string, unknown>; createdAt: string }

export const bankingApi = {
  async accounts(companyId: string): Promise<BankAccount[]> {
    const d = await request<{ accounts: BankAccount[] }>('/bank/accounts', { headers: companyHeader(companyId) });
    return d.accounts;
  },
  async addAccount(companyId: string, input: { name: string; provider: string; iban?: string | undefined; externalId?: string | undefined; fintsUrl?: string | undefined; fintsBankCode?: string | undefined; fintsUser?: string | undefined }): Promise<BankAccount> {
    const d = await request<{ account: BankAccount }>('/bank/accounts', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(input) });
    return d.account;
  },
  async removeAccount(companyId: string, accountId: string): Promise<void> {
    await request(`/bank/accounts/${accountId}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  /** Kontostand manuell setzen (Anfangsbestand/EB oder Korrektur). */
  async setBalance(companyId: string, accountId: string, balanceCents: number): Promise<void> {
    await request(`/bank/accounts/${accountId}/balance`, {
      method: 'PATCH', headers: companyHeader(companyId), body: JSON.stringify({ balanceCents }),
    });
  },
  /** Erfolgskonten (revenue|expense) fuer die Direktbuchungs-Auswahl. */
  async successAccounts(companyId: string, kind: 'revenue' | 'expense'): Promise<{ number: string; name: string }[]> {
    const d = await request<{ accounts: { number: string; name: string }[] }>(`/bank/success-accounts?kind=${kind}`, { headers: companyHeader(companyId) });
    return d.accounts;
  },
  /** Transaktion direkt als Betriebseinnahme/-ausgabe buchen (Eigenbeleg). */
  async bookTransaction(companyId: string, txId: string, input: { accountNumber: string; taxRate: number; description?: string }): Promise<BankTransaction> {
    const d = await request<{ transaction: BankTransaction }>(`/bank/transactions/${txId}/book`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(input),
    });
    return d.transaction;
  },
  async providers(companyId: string): Promise<Record<string, boolean>> {
    const d = await request<{ providers: Record<string, boolean> }>('/bank/providers', { headers: companyHeader(companyId) });
    return d.providers;
  },
  /** PayPal OAuth: liefert die Connect-URL, zu der der Nutzer weitergeleitet wird. */
  async paypalConnectUrl(companyId: string): Promise<string> {
    const d = await request<{ url: string }>('/bank/paypal/connect', { headers: companyHeader(companyId) });
    return d.url;
  },
  /** SumUp OAuth: liefert die Connect-URL, zu der der Nutzer weitergeleitet wird. */
  async sumupConnectUrl(companyId: string): Promise<string> {
    const d = await request<{ url: string }>('/bank/sumup/connect', { headers: companyHeader(companyId) });
    return d.url;
  },
  /** Kontoauszug (PDF, base64) importieren -> Konto per IBAN + Transaktionen. */
  async importStatement(companyId: string, data: string, mime: string, name?: string): Promise<StatementImportResult> {
    return request('/bank/statements/import', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ data, mime, ...(name ? { name } : {}) }),
    });
  },
  async searchBanks(companyId: string, q: string): Promise<{ blz: string; bic: string; name: string; ort: string; url: string }[]> {
    const d = await request<{ banks: { blz: string; bic: string; name: string; ort: string; url: string }[] }>(`/bank/fints-banks?q=${encodeURIComponent(q)}`, { headers: companyHeader(companyId) });
    return d.banks;
  },
  /**
   * Umsätze abrufen. `since` (ISO-Datum) holt gezielt einen älteren Zeitraum
   * nach — ohne Angabe wird ab dem letzten Abruf geholt.
   */
  async sync(companyId: string, accountId: string, pin?: string, since?: string): Promise<BankSyncResult> {
    return request(`/bank/accounts/${accountId}/sync`, {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ ...(pin ? { pin } : {}), ...(since ? { since } : {}) }),
    });
  },
  /** FinTS-TAN fortsetzen: tan leer lassen bei App-Freigabe (decoupled, Polling). */
  async tan(companyId: string, accountId: string, sessionId: string, tan?: string): Promise<BankSyncResult> {
    return request(`/bank/accounts/${accountId}/tan`, {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ sessionId, tan: tan ?? undefined }),
    });
  },
  async transactions(companyId: string, accountId: string): Promise<BankTransaction[]> {
    const d = await request<{ transactions: BankTransaction[] }>(`/bank/accounts/${accountId}/transactions`, { headers: companyHeader(companyId) });
    return d.transactions;
  },
  async addTransaction(companyId: string, accountId: string, input: { date: string; amountCents: number; purpose?: string; counterparty?: string }): Promise<void> {
    await request(`/bank/accounts/${accountId}/transactions`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(input) });
  },
  async suggestion(companyId: string, txId: string): Promise<MatchSuggestion | null> {
    const d = await request<{ suggestion: MatchSuggestion | null }>(`/bank/transactions/${txId}/suggestion`, { headers: companyHeader(companyId) });
    return d.suggestion;
  },
  async match(companyId: string, txId: string, kind: 'invoice' | 'receipt', targetId: string): Promise<BankTransaction> {
    const d = await request<{ transaction: BankTransaction }>(`/bank/transactions/${txId}/match`, {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ kind, targetId }),
    });
    return d.transaction;
  },
  async unmatch(companyId: string, txId: string): Promise<BankTransaction> {
    const d = await request<{ transaction: BankTransaction }>(`/bank/transactions/${txId}/unmatch`, { method: 'POST', headers: companyHeader(companyId) });
    return d.transaction;
  },
  async markPrivate(companyId: string, txId: string): Promise<BankTransaction> {
    const d = await request<{ transaction: BankTransaction }>(`/bank/transactions/${txId}/private`, { method: 'POST', headers: companyHeader(companyId) });
    return d.transaction;
  },
  async automatch(companyId: string, accountId: string): Promise<number> {
    const d = await request<{ matched: number }>(`/bank/accounts/${accountId}/automatch`, { method: 'POST', headers: companyHeader(companyId) });
    return d.matched;
  },
  async merchantLogos(companyId: string, merchants: string[]): Promise<Record<string, string | null>> {
    const d = await request<{ logos: Record<string, string | null> }>('/bank/merchant-logos', {
      method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ merchants }),
    });
    return d.logos;
  },
  async overdue(companyId: string): Promise<OverdueInvoice[]> {
    const d = await request<{ invoices: OverdueInvoice[] }>('/dunning/overdue', { headers: companyHeader(companyId) });
    return d.invoices;
  },
  async dun(companyId: string, invoiceId: string, opts?: { sendMail?: boolean; to?: string }): Promise<{ mail: MailResult | null }> {
    return request<{ mail: MailResult | null }>(`/dunning/${invoiceId}`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(opts ?? {}) });
  },
};

// ---- Nachweise (Reisekosten, VMA, Bewirtung) ----
export interface TravelExpense {
  id: string; date: string; fromAddress: string; toAddress: string; stops?: string[] | null;
  country: string; city: string | null; distanceKm: string | number; roundTrip: boolean; kmAmountCents: number;
  hoursAway: number | string | null; tripStart: string | null; tripEnd: string | null;
  vmaCents: number; lodgingCents: number; purpose: string | null;
}
export interface NewTravelInput {
  date: string; fromAddress: string; toAddress: string; stops?: string[] | null; country: string; city?: string | null; distanceKm: number;
  roundTrip: boolean; hoursAway?: number | null; tripStart?: string | null; tripEnd?: string | null;
  lodgingCents?: number | null; purpose?: string | null;
}
export interface PerDiemRate { full: number; partial: number; lodging: number }
/** Beleg am Reisetag, der mit der Pauschale kollidieren koennte (Doppelabzug). */
export interface TravelConflictHit {
  receiptId: string; date: string; vendor: string | null; grossCents: number;
  accountNumber: string | null; category: string | null;
  kind: 'fahrtkosten' | 'verpflegung' | 'uebernachtung'; reason: string;
}
export interface CountryOption { code: string; name: string }
export interface BewirtungProof {
  id: string; receiptId: string; mode: 'created' | 'uploaded';
  occasion: string | null; participants: string | null; location: string | null;
  date: string | null; fileKey: string | null; fileMime: string | null;
}
export interface BewirtungRow {
  receiptId: string; date: string; vendor: string | null; grossCents: number;
  hasProof: boolean; proof: BewirtungProof | null;
}

export const proofsApi = {
  async mapsAvailable(companyId: string): Promise<boolean> {
    const d = await request<{ available: boolean }>('/proofs/maps-available', { headers: companyHeader(companyId) });
    return d.available;
  },
  async distance(companyId: string, from: string, to: string, stops?: string[]): Promise<{ distanceKm: number; fromResolved: string; toResolved: string; etappen?: { von: string; nach: string; km: number }[] }> {
    // Stopps mit | verketten — Adressen enthalten Kommas, die taugen nicht
    // als Trenner.
    const stopParam = stops?.length ? `&stops=${encodeURIComponent(stops.join('|'))}` : '';
    return request(
      `/proofs/distance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${stopParam}`,
      { headers: companyHeader(companyId) },
    );
  },
  async addressSuggest(companyId: string, q: string): Promise<string[]> {
    const d = await request<{ results: { label: string }[] }>(`/proofs/address-suggest?q=${encodeURIComponent(q)}`, { headers: companyHeader(companyId) });
    return d.results.map((r) => r.label);
  },
  async travelList(companyId: string, year: number): Promise<{ year: number; items: TravelExpense[]; kmTotalCents: number; vmaTotalCents: number; lodgingTotalCents: number; totalCents: number; conflicts: Record<string, TravelConflictHit[]> }> {
    return request(`/proofs/travel?year=${year}`, { headers: companyHeader(companyId) });
  },
  async perdiem(companyId: string, opts?: { country?: string | undefined; city?: string | undefined; year?: number | undefined }): Promise<{ countries: CountryOption[]; cities?: CountryOption[]; rate?: PerDiemRate; approx?: boolean }> {
    const p = new URLSearchParams();
    if (opts?.country) p.set('country', opts.country);
    if (opts?.city) p.set('city', opts.city);
    if (opts?.year) p.set('year', String(opts.year));
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request(`/proofs/perdiem${qs}`, { headers: companyHeader(companyId) });
  },
  async travelCreate(companyId: string, input: NewTravelInput): Promise<TravelExpense> {
    const d = await request<{ item: TravelExpense }>('/proofs/travel', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify(input) });
    return d.item;
  },
  async travelUpdate(companyId: string, id: string, input: NewTravelInput): Promise<TravelExpense> {
    const d = await request<{ item: TravelExpense }>(`/proofs/travel/${id}`, { method: 'PATCH', headers: companyHeader(companyId), body: JSON.stringify(input) });
    return d.item;
  },
  async travelDelete(companyId: string, id: string): Promise<void> {
    await request(`/proofs/travel/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
  async travelConflicts(companyId: string, opts: { date: string; tripStart?: string | null; tripEnd?: string | null; lodgingCents?: number }): Promise<TravelConflictHit[]> {
    const p = new URLSearchParams({ date: opts.date });
    if (opts.tripStart) p.set('tripStart', opts.tripStart);
    if (opts.tripEnd) p.set('tripEnd', opts.tripEnd);
    if (opts.lodgingCents) p.set('lodgingCents', String(opts.lodgingCents));
    const d = await request<{ hits: TravelConflictHit[] }>(`/proofs/travel/conflicts?${p.toString()}`, { headers: companyHeader(companyId) });
    return d.hits;
  },
  async bewirtungList(companyId: string, year: number): Promise<{ year: number; rows: BewirtungRow[]; total: number; missing: number }> {
    return request(`/proofs/bewirtung?year=${year}`, { headers: companyHeader(companyId) });
  },
  async bewirtungSave(companyId: string, receiptId: string, input: { occasion?: string; participants?: string; location?: string; date?: string }): Promise<BewirtungProof> {
    const d = await request<{ proof: BewirtungProof }>(`/proofs/bewirtung/${receiptId}`, { method: 'PUT', headers: companyHeader(companyId), body: JSON.stringify(input) });
    return d.proof;
  },
  async bewirtungUpload(companyId: string, receiptId: string, data: string, mime: string): Promise<BewirtungProof> {
    const d = await request<{ proof: BewirtungProof }>(`/proofs/bewirtung/${receiptId}/upload`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ data, mime }) });
    return d.proof;
  },
  async bewirtungDelete(companyId: string, receiptId: string): Promise<void> {
    await request(`/proofs/bewirtung/${receiptId}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
};

export const formApi = {
  async list(companyId: string): Promise<TaxForm[]> {
    const d = await request<{ forms: TaxForm[] }>('/forms', { headers: companyHeader(companyId) });
    return d.forms;
  },
  async create(companyId: string, type: string, payload: Record<string, unknown>): Promise<TaxForm> {
    const d = await request<{ form: TaxForm }>('/forms', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ type, payload }) });
    return d.form;
  },
  async template(companyId: string, type: string): Promise<{ type: string; prefilled: Record<string, string> }> {
    return request(`/forms/template/${type}`, { headers: companyHeader(companyId) });
  },
  async draft(companyId: string, type: string, input: { keywords?: string; fields?: Record<string, string> }): Promise<string> {
    const d = await request<{ text: string }>('/forms/draft', { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ type, ...input }) });
    return d.text;
  },
  /** Antrags-PDF (Geschäftsbrief). preview=true -> inline anzeigbar. */
  async pdf(companyId: string, id: string, preview = false): Promise<Blob> {
    return requestBlob(`/forms/${id}/pdf${preview ? '?preview' : ''}`, companyId);
  },
  async remove(companyId: string, id: string): Promise<void> {
    await request(`/forms/${id}`, { method: 'DELETE', headers: companyHeader(companyId) });
  },
};

export interface CompanyFull {
  id: string; name: string; legalForm: string | null; taxNumber: string | null; vatId: string | null;
  kleinunternehmer: boolean; gewerbesteuer?: boolean; ustvaRhythmus?: 'monthly' | 'quarterly' | 'none' | null; street: string | null; postalCode: string | null; city: string | null;
  email: string | null; phone: string | null; iban: string | null; bic: string | null; logoDataUrl: string | null;
  industry?: string | null; chartOfAccounts?: string;
  invoiceDesign?: string; invoiceAccent?: string | null;
  invoiceNumberFormat?: string | null; quoteNumberFormat?: string | null;
  invoiceNumberStart?: number | null; quoteNumberStart?: number | null;
  skontoPercent?: number | string; skontoDays?: number | null;
}
export interface CompanyMemberItem { id: string; role: string; user: { id: string; email: string; name: string | null } }

export const profileApi = {
  async update(input: { name?: string; email?: string }): Promise<AuthUser> {
    const d = await request<{ user: AuthUser }>('/profile', { method: 'PATCH', body: JSON.stringify(input) });
    return d.user;
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request('/profile/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) });
  },
};

export const companySettingsApi = {
  async get(companyId: string): Promise<CompanyFull> {
    const d = await request<{ company: CompanyFull }>(`/companies/${companyId}`, { headers: companyHeader(companyId) });
    return d.company;
  },
  async update(companyId: string, input: Partial<CompanyFull>): Promise<CompanyFull> {
    const d = await request<{ company: CompanyFull }>(`/companies/${companyId}`, { method: 'PATCH', headers: companyHeader(companyId), body: JSON.stringify(input) });
    return d.company;
  },
  async members(companyId: string): Promise<CompanyMemberItem[]> {
    const d = await request<{ members: CompanyMemberItem[] }>(`/companies/${companyId}/members`, { headers: companyHeader(companyId) });
    return d.members;
  },
  async addMember(companyId: string, email: string, role: string): Promise<void> {
    await request(`/companies/${companyId}/members`, { method: 'POST', headers: companyHeader(companyId), body: JSON.stringify({ email, role }) });
  },
};

export const billingApi = {
  /** Startet Stripe-Checkout fuer einen Plan -> liefert Redirect-URL. */
  async checkout(
    companyId: string,
    plan: 'plus' | 'professional' | 'business',
    interval: 'monthly' | 'yearly',
  ): Promise<string> {
    const d = await request<{ url: string }>('/billing/checkout', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ plan, interval }),
    });
    return d.url;
  },
  /** Stripe Customer Portal (Abo verwalten) -> Redirect-URL. */
  async portal(companyId: string): Promise<string> {
    const d = await request<{ url: string }>('/billing/portal', {
      method: 'POST',
      headers: companyHeader(companyId),
    });
    return d.url;
  },
};

// ---- Partnerprogramm ----
export interface ReferralEntry {
  id: string;
  name: string;
  plan: string | null;
  registriertAm: string;
  /** NULL = noch nicht zahlend. */
  zahltSeit: string | null;
  /** Gutgeschriebene Praemie in Cent. */
  praemieCents: number | null;
}

export interface ReferralOverview {
  code: string;
  pfad: string;
  eingeladene: ReferralEntry[];
  summe: { gesamt: number; zahlend: number; praemieCents: number };
}

export const referralApi = {
  async me(): Promise<ReferralOverview> {
    return request<ReferralOverview>('/referrals/me');
  },
};

// ---- Steuerjahr-Freischaltung ----
export interface JahrStatus {
  jahr: number;
  status: 'im_abo' | 'freigeschaltet' | 'gesperrt';
  preisCents: number;
}

export const yearUnlockApi = {
  async list(companyId: string): Promise<{ preisCents: number; jahre: JahrStatus[] }> {
    return request('/billing/jahre', { headers: companyHeader(companyId) });
  },
  /** Startet den Kauf; liefert die Stripe-URL. */
  async kaufen(companyId: string, jahr: number): Promise<{ url: string; preisCents: number }> {
    return request('/billing/jahr-freischalten', {
      method: 'POST',
      headers: companyHeader(companyId),
      body: JSON.stringify({ jahr }),
    });
  },
};
